import { Box, Typography } from "@mui/material";

import { LegendPosition, SceneLegend } from "../../types/storyMap";
import { patternCss } from "./layerStyles";
import { LegendRow, LegendSwatch } from "./storyLegend";

/**
 * Legend card for a scene's layers. `variant` "dark" is the on-map overlay
 * (viewer sticky map + builder panel); "light" is the inline print-preview
 * version under a captured figure. Both render the same LegendRow model the
 * PDF builder draws, so the three stay in step.
 */

const DASH_CSS: Record<string, (color: string) => string> = {
  solid: (c) => c,
  dashed: (c) =>
    `repeating-linear-gradient(90deg, ${c} 0 6px, transparent 6px 10px)`,
  dotted: (c) =>
    `repeating-linear-gradient(90deg, ${c} 0 3px, transparent 3px 6px)`,
};

export function LegendSwatchView({
  swatch,
  variant = "dark",
}: {
  swatch: LegendSwatch;
  variant?: "dark" | "light";
}) {
  const border =
    variant === "dark" ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.25)";
  switch (swatch.kind) {
    case "fill":
      return (
        <Box
          sx={{
            width: 14,
            height: 14,
            flexShrink: 0,
            borderRadius: "2px",
            background: patternCss(swatch.pattern, swatch.color),
            opacity: swatch.pattern ? 1 : Math.max(0.25, swatch.opacity),
            border: `1.5px solid ${swatch.outline ?? swatch.color}`,
            boxShadow: `inset 0 0 0 1px ${border}`,
          }}
        />
      );
    case "line":
      return (
        <Box
          sx={{
            width: 22,
            height: Math.min(6, Math.max(2, swatch.width)),
            flexShrink: 0,
            borderRadius: "1px",
            background: (DASH_CSS[swatch.lineStyle] ?? DASH_CSS.solid)(swatch.color),
          }}
        />
      );
    case "point":
      return (
        <Box
          sx={{
            width: 10,
            height: 10,
            flexShrink: 0,
            borderRadius: "50%",
            bgcolor: swatch.color,
            border: `1px solid ${border}`,
          }}
        />
      );
    case "ramp":
      return (
        <Box
          sx={{
            width: 36,
            height: 10,
            flexShrink: 0,
            borderRadius: "2px",
            background: `linear-gradient(90deg, ${swatch.stops.join(", ")})`,
            border: `1px solid ${border}`,
          }}
        />
      );
  }
}

const POSITION_SX: Record<LegendPosition, Record<string, number>> = {
  "top-left": { top: 16, left: 16 },
  "top-right": { top: 16, right: 16 },
  "bottom-left": { bottom: 24, left: 16 },
  "bottom-right": { bottom: 24, right: 16 },
};

interface StoryLegendProps {
  rows: LegendRow[];
  legend?: SceneLegend | null;
  variant?: "dark" | "light";
  /** Absolute-position inside the map container (dark overlay only). */
  overlay?: boolean;
}

export default function StoryLegend({
  rows,
  legend,
  variant = "dark",
  overlay = true,
}: StoryLegendProps) {
  if (rows.length === 0) return null;
  const dark = variant === "dark";
  const text = dark ? "rgba(255,255,255,0.88)" : "#1c2530";
  const muted = dark ? "rgba(255,255,255,0.6)" : "#5a6572";
  const position = legend?.position ?? "bottom-right";

  const row = (label: string, swatch: LegendSwatch, indent = false, key?: string) => (
    <Box
      key={key ?? label}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: indent ? 1.5 : 0,
        minWidth: 0,
      }}
    >
      <LegendSwatchView swatch={swatch} variant={variant} />
      <Typography
        variant="caption"
        sx={{ color: indent ? muted : text, lineHeight: 1.3, whiteSpace: "nowrap" }}
      >
        {label}
      </Typography>
      {swatch.kind === "ramp" && (
        <Typography variant="caption" sx={{ color: muted, lineHeight: 1.3, whiteSpace: "nowrap" }}>
          {swatch.from} → {swatch.to}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box
      className="story-legend"
      sx={{
        ...(overlay && dark
          ? { position: "absolute", ...POSITION_SX[position], zIndex: 3, pointerEvents: "none" }
          : {}),
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        px: 1.5,
        py: 1,
        maxWidth: overlay && dark ? "min(280px, calc(100% - 32px))" : "none",
        maxHeight: overlay && dark ? "60%" : "none",
        overflow: "hidden",
        borderRadius: dark ? 2 : 1,
        bgcolor: dark ? "rgba(15, 23, 42, 0.88)" : "#f6f8fa",
        border: dark ? "none" : "1px solid #d5dbe2",
        boxShadow: dark ? "0 4px 16px rgba(0,0,0,0.4)" : "none",
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: dark ? "white" : "#1c2530", fontWeight: 700, lineHeight: 1.2 }}
      >
        {legend?.title?.trim() || "Legend"}
      </Typography>
      {rows.map((r) => (
        <Box key={r.id} sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
          {r.children && r.children.length > 0 ? (
            <>
              <Typography variant="caption" sx={{ color: text, lineHeight: 1.3 }}>
                {r.label}
              </Typography>
              {r.children.map((c, i) => row(c.label, c.swatch, true, `${r.id}-${i}`))}
            </>
          ) : (
            row(r.label, r.swatch, false, r.id)
          )}
        </Box>
      ))}
    </Box>
  );
}
